<template>
  <div
    id="performance"
    class="px-4"
  >
    <v-container
      v-if="eventParentDetails.loading"
      class="d-flex justify-center align-center my-16"
    >
      <v-progress-circular
        indeterminate
        size="100"
      />
    </v-container>
    <div v-else>
      <performance-header
        :event-data="eventParentDetails.data"
        :event-types="eventTypes"
        :has-periods="hasPeriods"
        :has-training-exercises-start-time="hasTrainingExercisesStartTime"
        :button-label="buttonLabel"
        :is-individual="isIndividual"
        :layouts="layouts"
        :selected-layout.sync="selectedLayout"
        @event-selection="eventSelection = true"
        @toggle-drawer="eventParentDetails.drawer = !eventParentDetails.drawer"
        @period-selection="periodSelection = true"
        @add-layout="openAddLayoutDialog"
        @edit-layout="openEditLayoutDialog"
      />

      <div v-if="isIndividual">
        <performance-individual
          v-if="eventData && eventData.players.length > 0 && selectedLayout"
          :key="eventData.id + ' ' + eventData.players[0].id"
          class="mt-4"
          :player="cloneDeep(eventData.players[0])"
          :team="eventParentDetails.data.teams[0]"
          :event="eventParentDetails"
          :variables="variables.data"
          :layout="selectedLayout"
          :selected-exercise="eventParentDetails.selectedExercise"
        />
      </div>
      <div v-else>
        <v-row
          style="padding: 10px"
          align="center"
          dense
        >
          <v-col>
            {{ $t("player_profile.analyze.label") }}
          </v-col>
          <v-col cols="auto">
            <v-btn
              v-for="aggregation in matchReferenceAggregations"
              :key="aggregation.value"
              small
              class="mx-1 rounded-xl text-none"
              :outlined="selectedMatchReferenceAggregation.value !== aggregation.value"
              :color="selectedMatchReferenceAggregation.value === aggregation.value ? 'primary' : ''"
              :text="selectedMatchReferenceAggregation.value !== aggregation.value"
              @click="selectedMatchReferenceAggregation = aggregation"
            >
              {{ aggregation.text }}
            </v-btn>
          </v-col>
        </v-row>
        <grid-layout
          v-if="selectedLayout"
          :key="selectedLayout.name"
          :layout="adjustedGridLayout"
          :col-num="12"
          :row-height="70"
          :is-draggable="false"
          :is-resizable="false"
          :vertical-compact="true"
          :margin="[10, 10]"
          :use-css-transforms="true"
          @layout-ready="layoutReady = true"
        >
          <grid-item
            v-for="item in adjustedGridLayout"
            :key="item.i"
            :x="item.x"
            :y="item.y"
            :w="item.w"
            :h="item.h"
            :i="item.i"
          >
            <div
              v-if="layoutReady"
              class="fill-height"
            >
              <div
                v-if="item.type === 'table'"
                class="fill-height"
              >
                <v-card class="fill-height rounded-lg">
                  <v-card-title class="px-6 py-4">
                    <v-row
                      no-gutters
                      align="center"
                    >
                      <v-col>
                        <div class="subtitle-2 grey--text text--grey-100">
                          {{ $t('event_details.players') }}
                        </div>
                      </v-col>
                      <template>
                        <v-col
                          cols="auto"
                          class="pl-2"
                        >
                          <v-text-field
                            v-model="playerName"
                            solo
                            dense
                            :label="$t('table_headers.name')"
                            hide-details
                            clearable
                            style="width: 200px"
                          />
                        </v-col>
                        <v-col
                          cols="auto"
                          class="pl-2"
                        >
                          <v-select
                            v-model="playerPositions.selectedId"
                            solo
                            dense
                            :label="$t('table_headers.player_overview.position')"
                            hide-details
                            clearable
                            item-value="id"
                            item-text="name"
                            :items="playerPositions.data"
                            style="width: 200px"
                          />
                        </v-col>
                        <v-col
                          cols="auto"
                          class="pl-2"
                        >
                          <v-select
                            v-model="playerStatuses.selectedId"
                            solo
                            dense
                            :label="$t('table_headers.player_overview.status')"
                            hide-details
                            clearable
                            :items="playerStatuses.data"
                            item-value="id"
                            item-text="name"
                            style="width: 200px"
                          />
                        </v-col>
                        <v-col
                          cols="auto"
                          class="pl-2"
                        >
                          <variable-selector
                            v-if="selectedLayout && variables.data"
                            :variables="variables.data"
                            :layout-variables.sync="tableHeaders"
                            @update="saveLayoutVariables"
                          />
                        </v-col>
                      </template>
                    </v-row>
                  </v-card-title>
                  <v-card-text class="pa-0">
                    <performance-general-table
                      :event-table="true"
                      :player-performance="searchFilteredPlayers"
                      :loading="eventDetails.loading"
                      :max-values="eventDetails.maxValues"
                      :table-headers="layoutVariables"
                      :colors-max-values="performanceMaxValues"
                      :reference-stats="selectedMatchReferenceAggregation.value !== null"
                      @select:player="$router.push({ name: RouteNames.PERFORMANCE_EVENT_PLAYER, params: { eventId: eventParentDetails.data.id, playerId: $event.id }, query: {exerciseId: eventParentDetails.selectedExercise ? eventParentDetails.selectedExercise.id : undefined } })"
                    />
                  </v-card-text>
                </v-card>
              </div>
              <div
                v-if="item.type === 'diagnostic_table'"
                class="fill-height"
              >
                <v-card class="rounded-lg fill-height">
                  <diagnostic-results-table
                    :diagnostic-stats="diagnosticStats.data"
                    :loading="diagnosticStats.loading"
                    :available-variables="diagnosticVariables.data"
                    :selected-variables.sync="diagnosticVariables.selected"
                    @update:columns="updateSelectedDiagnosticVariables"
                  />
                </v-card>
              </div>
              <div
                v-else-if="item.type === 'card'"
                class="fill-height"
              >
                <stat-card
                  class="fill-height"
                  :loading="eventDetails.loading"
                  :title="getVariableName(item.key)"
                  :value="currentTeamStats[item.key]"
                  :max="performanceMaxValues[item.key] || eventDetails.maxValues[item.key]"
                  :color="getColorFromValue(currentTeamStats[item.key], performanceMaxValues[item.key] || eventDetails.maxValues[item.key], reversedVariables().includes(item.key))"
                >
                  {{ currentTeamStats[item.key] }}{{ selectedMatchReferenceAggregation.value ? '%' : '' }}
                </stat-card>
              </div>
              <div
                v-else-if="item.type === 'chart'"
                class="fill-height"
              >
                <performance-graph-individual
                  :variables="variables.data"
                  :chart="item"
                  :stats="currentTeamStats"
                  :height="String(item.h * 70 - 50)"
                />
                <performance-graph
                  class="fill-height"
                  :players="searchFilteredPlayers"
                  :chart="item"
                  :stats="currentTeamStats"
                />
              </div>
            </div>
          </grid-item>
        </grid-layout>
      </div>
    </div>

    <event-sidebar
      v-if="eventParentDetails.data"
      :event-details.sync="eventParentDetails.data"
      :loading="eventParentDetails.loading"
      :value.sync="eventParentDetails.drawer"
      :event-view="false"
      @update="fetchLatestEvent(eventParentDetails.data.id, false)"
    />

    <card-dialog
      v-model="periodSelection"
      :value="periodSelection"
      :persistent="false"
      max-width="700px"
    >
      <template v-slot:title>
        <v-card-title
          class="blue-grey lighten-5 px-6 py-4"
        >
          <div>
            {{ $t("card_dialog.select_period.title") }}
          </div>
        </v-card-title>
      </template>

      <v-card-text class="pa-0">
        <div class="pa-4">
          <v-list
            v-if="eventParentDetails.data"
            class="v-list--bordered ma-0 pa-0"
          >
            <v-list-item
              :class="{ 'v-list-item--active': eventParentDetails.selectedPeriodId === null && !eventParentDetails.selectedExercise }"
              @click="periodSelection = false; eventParentDetails.selectedPeriodId = null; eventParentDetails.selectedExercise = null"
            >
              <v-list-item-content>
                <v-list-item-title
                  :class="{ 'font-weight-bold primary--text': eventParentDetails.selectedPeriodId === null && !eventParentDetails.selectedExercise}"
                >
                  {{ $t('card_dialog.select_period.entire_event', { event_type: eventTypes[eventParentDetails.data.event_type] ? eventTypes[eventParentDetails.data.event_type].text : '' }) }}
                </v-list-item-title>
                <v-list-item-subtitle class="mt-1">
                  {{ $dayjs(eventParentDetails.data.start_at).format(getDateTimeFormatForUser('ddd, MMMM D, HH:mm')) }} - {{ $dayjs(eventParentDetails.data.end_at).format(getDateTimeFormatForUser('HH:mm')) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <v-btn-toggle
            v-if="hasPeriods && hasTrainingExercisesStartTime"
            v-model="advancedView"
            class="v-btn-toggle--block my-2"
            mandatory
            color="primary"
          >
            <v-btn value="period">
              {{ $t('button.period') }}
            </v-btn>
            <v-btn value="exercise">
              {{ $t('button.exercise') }}
            </v-btn>
          </v-btn-toggle>
          <v-divider v-else />
          <div v-if="advancedView === 'period'">
            <v-list
              v-if="hasPeriods"
              class="v-list--bordered ma-0 pa-0"
            >
              <v-list-item
                v-for="period in eventParentDetails.data.periods"
                :key="period.id"
                :class="{ 'v-list-item--active': eventParentDetails.selectedPeriodId && eventParentDetails.selectedPeriodId === period.id}"
                @click="periodSelection = false; eventParentDetails.selectedPeriodId = period.id"
              >
                <v-list-item-content>
                  <v-list-item-title
                    :class="{ 'font-weight-bold primary--text': eventParentDetails.selectedPeriodId && eventParentDetails.selectedPeriodId === period.id }"
                  >
                    {{ period.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="mt-1">
                    {{ $dayjs(period.start_at).format(getDateTimeFormatForUser('ddd, MMMM D, HH:mm')) }} - {{ $dayjs(period.end_at).format(getDateTimeFormatForUser('HH:mm')) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
          <div v-else>
            <v-list
              v-if="eventParentDetails.data && eventParentDetails.data.training_exercises && eventParentDetails.data.training_exercises.length > 0"
              class="v-list--bordered ma-0 pa-0"
            >
              <div
                v-for="exercise in eventTrainingExercises"
                :key="exercise.id"
              >
                <div v-if="exercise.type === 'superset' || exercise.type === 'pause' || exercise.type === 'exercise' ">
                  <exercise-list-item
                    :exercise="exercise"
                    :active="eventParentDetails.selectedExercise && eventParentDetails.selectedExercise.id === exercise.id"
                    @handle:click="periodSelection = false; eventParentDetails.selectedExercise = exercise"
                  />
                </div>
                <div v-else>
                  <div
                    v-for="subExercise in exercise.exercises"
                    :key="subExercise.id"
                  >
                    <exercise-list-item
                      :exercise="subExercise"
                      :active="eventParentDetails.selectedExercise && eventParentDetails.selectedExercise.id === exercise.id"
                    />
                  </div>
                </div>
              </div>
            </v-list>
          </div>
        </div>
      </v-card-text>
    </card-dialog>

    <card-dialog
      v-model="eventSelection"
      :value="eventSelection"
      :persistent="false"
      :title="$t('select_event.label')"
      max-width="700px"
    >
      <template v-slot:title>
        <v-card-title
          class="blue-grey lighten-5 px-6 py-4"
        >
          <div>
            {{ $t("select_event.label") }}
          </div>
          <v-row
            class="mt-3"
            dense
          >
            <v-col>
              <v-text-field
                v-model="filters.eventName"
                solo
                dense
                :label="$t('table_headers.name')"
                hide-details
                clearable
              />
            </v-col>
            <v-col>
              <v-select
                v-model="filters.eventType"
                solo
                dense
                :label="$t('dropdown.event_type.title')"
                hide-details
                clearable
                item-value="type"
                item-text="name"
                :items="[{type: eventTypeTraining, name: $t('event_type.training')}, {type: eventTypeMatch, name: $t('event_type.match')}]"
              />
            </v-col>
            <v-col>
              <v-menu
                v-model="filterDateFromActive"
                :close-on-content-click="true"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="dateFromText"
                    class="custom-height-text-field"
                    :label="$t('date_from.label')"
                    solo
                    dense
                    clearable
                    hide-details
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="filters.dateFrom"
                  :first-day-of-week="1"
                  no-title
                  :max="$dayjs().format('YYYY-MM-DD')"
                  @change="filterDateFromActive = false; events.pagination.current_page = 1; fetchEvents(false)"
                />
              </v-menu>
            </v-col>
            <v-col>
              <v-menu
                v-model="filterDateToActive"
                :close-on-content-click="true"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="dateToText"
                    class="custom-height-text-field"
                    :label="$t('date_to.label')"
                    solo
                    dense
                    clearable
                    hide-details
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="filters.dateTo"
                  :first-day-of-week="1"
                  no-title
                  :max="$dayjs().format('YYYY-MM-DD')"
                  @change="filterDateToActive = false; events.pagination.current_page = 1; fetchEvents(false)"
                />
              </v-menu>
            </v-col>
          </v-row>
        </v-card-title>
      </template>

      <template v-slot:actions>
        <v-card-actions
          v-if="events.pagination && events.pagination.last_page > 1"
          class="d-flex justify-center align-center py-5"
        >
          <pagination
            v-model="events.pagination.current_page"
            :last-page="events.pagination.last_page"
          />
        </v-card-actions>
      </template>
      <v-card-text class="pa-0">
        <div style="height: 1px">
          <v-progress-linear
            v-if="events.loading"
            indeterminate
            height="1px"
          />
        </div>

        <div
          class="pa-4"
          style="min-height: 300px"
        >
          <v-list
            v-if="events.data && events.data.length > 0"
            class="v-list--bordered ma-0 pa-0"
          >
            <v-list-item
              v-for="event in events.data"
              :key="event.id"
              :class="{ 'v-list-item--active': eventParentDetails.data.id === event.id }"
              @click="fetchLatestEvent(event.id);eventSelection = false"
            >
              <v-list-item-content>
                <v-list-item-title
                  :class="{ 'font-weight-bold primary--text': eventParentDetails.data.id === event.id }"
                >
                  {{ event.title }}
                </v-list-item-title>
                <v-list-item-subtitle class="mt-1">
                  {{ $dayjs(event.start_at).format('ddd, MMMM D, HH:mm') }}
                  &middot;
                  {{ event.teams.map(t => t.short_name).join(', ') }}
                  &middot;
                  {{ $tc("players.count", event.player_ids.length, {count: event.player_ids.length}) }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-chip
                  class="ml-2 mb-1"
                  label
                  :color="eventTypes[event.event_type].color"
                  dark
                  small
                >
                  {{ eventTypes[event.event_type].text }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <v-row
            v-else-if="!events.loading"
            justify="center"
          >
            <v-col cols="auto">
              {{ $t("select_event.no_events_match_filter") }}
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </card-dialog>
  </div>
</template>

<script>
import {
  EventApiService,
  PlayerApiService,
  PlayerPositionApiService,
  PlayerStatusApiService,
  TeamApiService
} from '@/data/services'
import PerformanceGeneralTable from '@/components/performance/PerformanceGeneralTable'
import StatCard from '@/components/StatCard'
import {
  adjustGridForTableHeights,
  getColorForValue,
  getDateTimeFormatForUser,
  processMaxValues,
  processMinValues,
  toCamelCase
} from '@/utils/helpers'
import { EventType, EventTypes } from '@/data/models'
import PerformanceGraph from '@/components/performance/PerformanceGraph.vue'
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'
import {
  getMaxValuesPerformanceMatch,
  getMaxValuesPerformanceTraining,
  reversedVariables
} from '@/data/models/TableHeader'
import { cloneDeep, orderBy } from 'lodash'
import EventSidebar from '@/components/events/EventSidebar.vue'
import CardDialog from '@/components/helpers/CardDialog.vue'
import { DashboardType } from '@/data/models/Dashboard'
import PerformanceIndividual from '@/components/performance/PerformanceIndividual.vue'
import VariableSelector from '@/components/performance/VariableSelector.vue'
import { EventBus } from '@/plugins/bus'
import { LayoutApiService } from '@/data/services/LayoutApiService'
import { GridItem, GridLayout } from 'vue-grid-layout'
import { VariableApiService } from '@/data/services/VariableApiService'
import ExerciseListItem from '@/components/training-builder/ExerciseListItem.vue'
import Pagination from '@/components/Pagination.vue'
import PerformanceGraphIndividual from '@/components/performance/PerformanceGraphIndividual.vue'
import { ChartApiService } from '@/data/services/ChartApiService'
import PerformanceHeader from '@/components/performance/PerformanceHeader.vue'
import { RouteNames } from '@/router/routes'
import DiagnosticResultsTable from '@/components/diagnostics/DiagnosticResultsTable.vue'
import { dashboardConfig } from '@/data/models/DashboardConfig'

export default {
  name: 'PerformanceEventView',
  // eslint-disable-next-line vue/no-unused-components
  components: {
    DiagnosticResultsTable,
    PerformanceHeader,
    PerformanceGraph,
    PerformanceGraphIndividual,
    Pagination,
    ExerciseListItem,
    VariableSelector,
    PerformanceIndividual,
    CardDialog,
    EventSidebar,
    StatCard,
    PerformanceGeneralTable,
    GridLayout,
    GridItem
  },
  data() {
    return {
      advancedView: 'period',
      eventSelection: false,
      periodSelection: false,
      date: dayjs().format('YYYY-MM-DD'),
      events: {
        loading: false,
        data: undefined,
        pagination: {
          per_page: 10,
          current_page: 1,
          last_page: null
        }
      },
      eventDetails: {
        loading: false,
        data: {},
        maxValues: {}
      },
      eventParentDetails: {
        loading: false,
        data: {},
        periods: [],
        maxValues: {},
        selectedPeriodId: null,
        selectedExercise: null,
        drawer: false
      },
      matchReferenceStats: {
        loading: false,
        data: [],
        maxValues: {}
      },
      players: {
        loading: false,
        data: [],
        maxValues: {},
        minValues: {}
      },
      player: {
        selected: undefined,
        rawSelected: undefined,
        tab: 'sprints',
        selectedBands: [3, 4, 5, 6, 7],
        loadingRawData: false
      },
      playerStatuses: {
        data: [],
        loading: false,
        loaded: false,
        selectedId: undefined
      },
      playerPositions: {
        data: [],
        loading: false,
        loaded: false,
        selectedId: undefined
      },
      eventPeriods: {
        data: [],
        loading: false,
        maxValues: {}
      },
      variables: {
        data: [],
        loading: false
      },
      diagnosticVariables: {
        data: [],
        selected: [],
        loading: false
      },
      charts: {
        data: [],
        loading: false
      },
      teamStats: {
        data: [],
        loading: false
      },
      diagnosticStats: {
        data: [],
        loading: false
      },
      playerName: '',
      filters: {
        eventType: undefined,
        dateFrom: dayjs().subtract(3, 'month').format('YYYY-MM-DD'),
        dateTo: dayjs().format('YYYY-MM-DD'),
        eventName: ''
      },
      filterDateFromActive: false,
      filterDateToActive: false,
      playerIndividualDialog: false,
      customizeModal: false,
      dashboard: DashboardType.TEAM_EVENT_PERFORMANCE,
      layoutReady: false,
      selectedLayoutObject: null,
      selectedVariablesObject: null,
      selectedMatchReferenceAggregation: { value: null, text: 'Values' }
    }
  },
  computed: {
    RouteNames() {
      return RouteNames
    },
    DashboardType() {
      return DashboardType
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasLayoutManagement() {
      return this.user && this.user.organisation_settings?.has_layout_management
    },
    currentTeamStats() {
      if (this.selectedMatchReferenceAggregation.value) {
        const players = this.matchReferenceStats.data || []

        if (players.length === 0) return {}

        const statKeys = Object.keys(players[0]?.player_stats || {})

        return statKeys.reduce((averageStats, statKey) => {
          const values = players
            .map(p => p.player_stats[statKey])
            .filter(v => v != null && !isNaN(v))

          averageStats[statKey] = values.length > 0
            ? Math.round((values.reduce((sum, v) => sum + v, 0) / values.length) * 100) / 100
            : 0

          return averageStats
        }, {})
      } if (this.eventParentDetails.selectedExercise && this.eventDetails.data.group) {
        return this.eventDetails.data.group || {}
      } else if (this.eventParentDetails.selectedPeriodId) {
        if (!this.eventDetails.data || !this.eventDetails.data.teams || !this.eventDetails.data.teams[0].team_stats) return {}
        else return this.eventDetails.data.teams[0].team_stats
      }

      return {
        ...(this.eventParentDetails?.data?.teams?.[0]?.team_stats ?? {}),
        ...(this.teamStats?.data?.[0]?.stats ?? {}),
        reason: (this.searchFilteredPlayers.filter(player => player.has_attended).length / this.searchFilteredPlayers.length * 100).toFixed(0) + '%'
      }
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    isIndividual() {
      return this.eventData.players && this.eventData.players.length === 1
    },
    hasPeriods() {
      return this.eventParentDetails.data && this.eventParentDetails.data.periods && this.eventParentDetails.data.periods.length > 0
    },
    hasTrainingExercisesStartTime() {
      if (this.eventParentDetails.data && this.eventParentDetails.data.players && this.eventParentDetails.data.players.length > 1) {
        return this.eventParentDetails.data?.training_exercises?.some(exercise => {
          if (exercise.exercise_start_at) {
            return true
          }
          if (exercise.type === 'group' && exercise.exercises?.some(subExercise => subExercise.exercise_start_at)) {
            return true
          }
        })
      } else if (this.eventParentDetails.data && this.eventParentDetails.data.players && this.eventParentDetails.data.players.length === 1) {
        return this.eventParentDetails.data?.players[0].training_exercises?.some(exercise => {
          if (exercise.exercise_start_at) {
            return true
          }
          if (exercise.type === 'group' && exercise.exercises?.some(subExercise => subExercise.exercise_start_at)) {
            return true
          }
        })
      }
      return false
    },
    searchFilteredPlayers() {
      if (!this.eventData.players) return []

      const filteredPlayers = this.eventData.players.filter(player => {
        if (this.playerPositions.selectedId) return player.player_positions.map(p => p.id).indexOf(this.playerPositions.selectedId) > -1

        return player
      }).filter(player => {
        if (this.playerStatuses.selectedId) return player.player_status_id === this.playerStatuses.selectedId

        return player
      }).filter(player => {
        if (this.playerName) return (player.first_name + player.last_name).toLowerCase().includes(this.playerName.toLowerCase())

        return player
      })

      return filteredPlayers.map(player => {
        const playerWithStats = this.players?.data?.find(p => p.id === player.id)
        return {
          ...player,
          player_stats: {
            ...(player.player_stats || {}),
            ...(playerWithStats?.latest_stats || {})
          }
        }
      })
    },
    eventTrainingExercises() {
      let exercises = []
      if (this.eventParentDetails.data && this.eventParentDetails.data.players.length > 1) {
        exercises = this.eventParentDetails.data.training_exercises
      } else if (this.eventParentDetails.data && this.eventParentDetails.data.players.length === 1) {
        exercises = this.eventParentDetails.data.players[0].training_exercises
      }

      const result = []

      exercises.forEach(exercise => {
        if (exercise.type === 'group' && Array.isArray(exercise.exercises)) {
          result.push(...exercise.exercises)
        } else {
          result.push(exercise)
        }
      })

      return result
    },
    integrations() {
      return this.user.organisation_settings.integrations || []
    },
    performanceMaxValues() {
      if (this.selectedMatchReferenceAggregation.value) return this.matchReferenceStats.maxValues

      return (this.eventDetails.data && this.eventDetails.data.event_type === 'training') ? this.colorMaxValuesTraining : this.colorMaxValuesMatch
    },
    dateFromText() {
      return this.$dayjs(this.filters.dateFrom).format('ll')
    },
    dateToText() {
      return this.$dayjs(this.filters.dateTo).format('ll')
    },
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    layoutVariables() {
      return this.selectedLayout ? [{ sortable: false, class: 'pr-0' }, { value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.tableHeaders.map(header => {
        return {
          ...header,
          text: this.$t(`variables.${header.value}`)
        }
      }), ['order'])) : []
    },
    layoutsDashboard() {
      return this.$store.getters[this.$storeGetters.APP_LAYOUTS]
        .filter(layout => layout.dashboard === DashboardType.TEAM_EVENT_PERFORMANCE)
    },
    layouts() {
      if (this.isIndividual) {
        return this.$store.getters[this.$storeGetters.APP_LAYOUTS]
          .filter(layout => layout.dashboard === DashboardType.PLAYER_PERFORMANCE)
      } else {
        return this.$store.getters[this.$storeGetters.APP_LAYOUTS]
          .filter(layout => layout.dashboard === DashboardType.TEAM_EVENT_PERFORMANCE)
      }
    },
    selectedLayout: {
      get() {
        if (this.selectedLayoutObject) {
          return this.selectedLayoutObject
        }
        return this.layouts[0]
      },
      set(value) {
        if (value !== this.selectedLayoutObject) this.layoutReady = false
        this.selectedLayoutObject = value
      }
    },
    tableHeaders: {
      get() {
        if (this.selectedVariablesObject) {
          return this.selectedVariablesObject
        } else if (this.selectedLayout) {
          return this.selectedLayout.variables
        }
        return []
      },
      set(value) {
        this.selectedVariablesObject = value
      }
    },
    buttonLabel() {
      if (this.eventParentDetails.selectedExercise) {
        return this.eventParentDetails.selectedExercise.name || 'Superset'
      } else if (!this.eventParentDetails.selectedPeriodId) {
        return this.$t('card_dialog.select_period.entire_event', { event_type: this.eventTypes[this.eventParentDetails.data.event_type].text })
      } else {
        return this.eventDetails.data.title
      }
    },
    adjustedGridLayout() {
      if (!this.selectedLayout || !this.selectedLayout.grid) return []

      const regularTableRowCount = this.searchFilteredPlayers?.length || 0

      const diagnosticTableRowCount = this.diagnosticStats?.data?.length || 0

      return adjustGridForTableHeights(this.selectedLayout.grid, regularTableRowCount, diagnosticTableRowCount)
    },
    eventData() {
      if (this.selectedMatchReferenceAggregation.value) {
        return {
          ...this.eventParentDetails.data,
          players: this.matchReferenceStats.data
        }
      } else if (this.eventParentDetails.selectedExercise) {
        return this.eventDetails.data
      } else if (this.eventParentDetails.selectedPeriodId) {
        return this.eventDetails.data
      } else {
        return this.eventParentDetails.data
      }
    },
    colorMaxValuesTraining() {
      return getMaxValuesPerformanceTraining(this.user.organisation_settings?.unit_system)
    },
    colorMaxValuesMatch() {
      return getMaxValuesPerformanceMatch(this.user.organisation_settings?.unit_system)
    },
    matchReferenceAggregations() {
      return [
        { value: null, text: this.$t('button.values') },
        { value: 'avg_true', text: this.$t('button.avg_true') },
        { value: 'ponder_true', text: this.$t('button.ponder_true') },
        { value: 'avg_false', text: this.$t('button.avg_false') },
        { value: 'ponder_false', text: this.$t('button.ponder_false') }
      ]
    }
  },
  watch: {
    'eventParentDetails.selectedExercise': function (val) {
      if (val) {
        this.eventParentDetails.selectedPeriodId = null
        this.fetchStatsByExercise()
      }
    },
    currentTeam() {
      this.fetchEvents(true)
    },
    'filters.eventName': function() {
      this.events.pagination.current_page = 1
      this.fetchEventsDebounced()
    },
    'filters.eventType': function () {
      this.events.pagination.current_page = 1
      this.fetchEvents()
    },
    'eventParentDetails.data': function () {
      if (!this.hasPeriods && this.hasTrainingExercisesStartTime) this.advancedView = 'exercise'
    },
    'eventParentDetails.selectedPeriodId': function (val) {
      if (val === null) return

      this.eventParentDetails.selectedExercise = null
      this.fetchPeriod()
    },
    eventSelection(val) {
      if (val) this.fetchEvents()
    },
    'events.pagination.current_page': function (val) {
      this.fetchEvents()
    },
    'events.selectedEvent': function (val) {
      if (val) this.fetchEventDetails()
    },
    selectedLayout() {
      this.selectedVariablesObject = null
    },
    selectedMatchReferenceAggregation() {
      if (this.selectedMatchReferenceAggregation.value) {
        this.fetchMatchReferenceStats()
      } else {
        this.matchReferenceStats.data = []
      }
    }
  },
  mounted() {
    EventBus.$on(this.$events.EVENT_DELETED, (event, shouldFetch = true) => {
      if (shouldFetch) this.fetchEvents(true)
    })

    EventBus.$on(this.$events.EVENT_UPDATED, (event) => {
      if (this.eventDetails.data && this.eventDetails.data.id === event.id) {
        this.fetchEventDetails()
      }
    })
  },
  created() {
    this.eventTypeMatch = EventType.MATCH
    this.eventTypeTraining = EventType.TRAINING

    this.eventTypes = EventTypes

    EventBus.$on(this.$events.EVENT_DELETED, (event, shouldFetch = true) => {
      if (shouldFetch) this.fetchLatestEvent()
    })

    EventBus.$on(this.$events.EVENT_UPDATED, (event) => {
      this.fetchLatestEvent(event.id)
    })

    EventBus.$on(this.$events.LAYOUT_UPDATED, (event) => {
      this.selectedLayoutObject = null
    })

    EventBus.$on(this.$events.LAYOUT_DELETED, (layout) => {
      this.selectedLayoutObject = this.layouts.filter(l => l.id !== layout.id)[0]
    })

    EventBus.$on(this.$events.LAYOUT_UPDATED, () => {
      this.selectedLayoutObject = null
    })

    const eventId = parseInt(this.$route.query.event_id)
    if (eventId) {
      this.fetchLatestEvent(eventId)
      this.$router.replace({ query: {} })
    } else {
      this.fetchLatestEvent()
    }

    this.fetchVariables()
    this.fetchCharts()
    this.fetchPlayerPositions()
    this.fetchPlayerStatuses()
  },
  beforeDestroy() {
    EventBus.$off(this.$events.EVENT_DELETED)
    EventBus.$off(this.$events.EVENT_UPDATED)
  },
  methods: {
    getDateTimeFormatForUser,
    dayjs,
    reversedVariables() {
      return reversedVariables
    },
    cloneDeep,
    orderBy,
    fetchEvents: async function (forceFirst = false, type = EventType.TRAINING) {
      this.events.loading = true

      try {
        const response = await EventApiService.getAll({
          order_by: 'start_at',
          order: 'desc',
          only_own_events: true,
          from: this.filters.dateFrom,
          to: this.filters.dateTo,
          team_id: this.currentTeam.id,
          search: this.filters.eventName,
          type: this.filters.eventType,
          compact: true,
          per_page: this.events.pagination.per_page,
          page: this.events.pagination.current_page
        })

        this.events.data = response.data
        this.events.pagination = response.meta
      } catch (e) {
        this.events.data = []
        throw e
      } finally {
        this.events.loading = false
      }
    },
    fetchEventsDebounced: debounce(function () {
      this.fetchEvents()
    }, 500),
    async fetchPeriod() {
      this.eventDetails.loading = true
      this.advancedView = 'period'

      try {
        const response = await EventApiService.getEventDetails(this.eventParentDetails.selectedPeriodId)

        this.eventDetails.data = response.data
        this.eventDetails.maxValues = processMaxValues(response.data.players.map(player => player.player_stats || {}))
      } catch (e) {
        this.events.data = []
        throw e
      } finally {
        this.eventDetails.loading = false
      }
    },
    async fetchLatestEvent(eventId = null) {
      this.eventParentDetails.loading = true
      this.eventParentDetails.selectedPeriodId = null
      this.eventParentDetails.selectedExercise = null
      this.advancedView = 'period'

      try {
        let response = null

        if (eventId) {
          response = await EventApiService.getEventDetails(eventId)
        } else {
          response = await EventApiService.getLatestEvent({
            only_own_events: true,
            team_id: this.currentTeam.id
          })
        }

        const data = response.data

        this.eventParentDetails.data = data

        this.eventParentDetails.periods = data.periods
        this.eventParentDetails.maxValues = processMaxValues(data.players.map(player => player.player_stats || {}))

        await this.fetchDiagnosticEventStats()
        await this.fetchTeamStats()
        await this.fetchPlayers()
      } catch (e) {
        this.events.data = []
        throw e
      } finally {
        this.eventParentDetails.loading = false
      }
    },
    fetchTeamStats: debounce(async function() {
      if (this.currentTeam.id === 'all' || !this.currentTeam.id || !this.eventParentDetails.data.id) return

      this.teamStats.loading = true

      try {
        const teamStats = await TeamApiService.getStats(this.currentTeam.id, dayjs(this.eventParentDetails.data.start_at).format('YYYY-MM-DD'), dayjs(this.eventParentDetails.data.start_at).format('YYYY-MM-DD'))

        this.teamStats.data = teamStats.map(stat => {
          if (!stat.stats) {
            return stat
          }

          if (stat.stats.SLD) stat.stats.SLD = (stat.stats.SLD / 60).toFixed(2)

          if (stat.stats.FAT) stat.stats.FAT_perc = (((6 - stat.stats.FAT) / 5) * 100).toFixed(0)
          if (stat.stats.GMS) stat.stats.GMS_perc = (((6 - stat.stats.GMS) / 5) * 100).toFixed(0)
          if (stat.stats.MOO) stat.stats.MOO_perc = ((stat.stats.MOO / 5) * 100).toFixed(0)
          if (stat.stats.STL) stat.stats.STL_perc = (((6 - stat.stats.STL) / 5) * 100).toFixed(0)

          return stat
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: this.$t('alert.pulling_team_stats.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.teamStats.loading = false
      }
    }),
    fetchPlayers: debounce(async function() {
      try {
        this.players.loading = true

        const queryParams = {
          team_id: this.currentTeam.id === 'all' ? undefined : this.currentTeam.id,
          stats_date: this.$dayjs(this.eventParentDetails.data.start_at).format('YYYY-MM-DD')
        }

        const response = await PlayerApiService.getAll(queryParams)

        const players = response.data.map(player => {
          if (!player.latest_stats) {
            return player
          }

          if (player.latest_stats.SLD) player.latest_stats.SLD = (player.latest_stats.SLD / 60).toFixed(2)

          return player
        })

        this.players.maxValues = processMaxValues(players.map(player => player.latest_stats))
        this.players.minValues = processMinValues(players.map(player => player.latest_stats))

        this.players.data = players
      } catch (e) {
        this.players.data = []
        throw e
      } finally {
        this.players.loading = false
      }
    }, 50),
    getColorFromValue(value, max, reverse = false) {
      return getColorForValue(value, max, reverse)
    },
    async fetchPlayerPositions() {
      if (this.playerPositions.loaded) return

      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll({ sport_id: this.currentTeam.sport.id })

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchPlayerStatuses() {
      if (this.playerStatuses.loaded) return

      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data.map((status) => {
          return {
            id: status.id,
            name: this.$t(`player_status.${status.name}`)
          }
        })
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerStatuses.loading = false
      }
    },
    async fetchStatsByExercise() {
      if (this.eventDetails.loading) return

      try {
        this.eventDetails.loading = true

        const params = {
          player_ids: this.eventParentDetails.data.players.map(p => p.id),
          event_ids: [this.eventParentDetails.data.id],
          training_protocol_exercise_ids: this.eventParentDetails.selectedExercise.type === 'superset' ? [this.eventParentDetails.selectedExercise.exercises[0].internal_id] : [this.eventParentDetails.selectedExercise.internal_id]
        }

        const result = await EventApiService.getAdvancedPerformanceStats(params)

        this.eventDetails.data = result

        this.eventDetails.maxValues = processMaxValues(result.players.map(player => player.player_stats || {}))
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_advanced_statistics'),
          color: 'error'
        })

        throw e
      } finally {
        this.eventDetails.loading = false
      }
    },
    async fetchVariables() {
      this.variables.loading = true

      try {
        const result = await VariableApiService.getVariables({
          category: dashboardConfig[DashboardType.TEAM_EVENT_PERFORMANCE].variable_categories
        })

        this.variables.data = result.data.map(v => {
          return {
            ...v,
            text: v.suffix ? this.$t(`variables.${v.value}`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}`),
            long_text: v.suffix ? this.$t(`variables.${v.value}.long`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}.long`)
          }
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.variables.loading = false
      }
    },
    async fetchCharts() {
      try {
        this.charts.loading = true

        const response = await ChartApiService.getCharts()

        this.charts.data = response.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.variables_fetch.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.charts.loading = false
      }
    },
    async fetchDiagnosticEventStats() {
      if (!this.eventParentDetails.data.diagnostic_protocol_id) return

      this.diagnosticStats.loading = true

      try {
        const response = await EventApiService.getDiagnosticStats(this.eventParentDetails.data.id)

        this.diagnosticStats.data = response

        this.diagnosticVariables.data = response.map(stat => stat.variables).flat().reduce((acc, variable) => {
          if (!acc.some(v => v.var_id === variable.var_id)) {
            acc.push(variable)
          }
          return acc
        }, [])
        this.diagnosticVariables.selected = this.diagnosticVariables.data.slice(0, 3)
      } catch (e) {
        this.diagnosticStats.data = []
      } finally {
        this.diagnosticStats.loading = false
      }
    },
    async fetchMatchReferenceStats() {
      this.matchReferenceStats.loading = true

      try {
        const data = {
          aggregation: this.selectedMatchReferenceAggregation.value.split('_')[0],
          full_game: this.selectedMatchReferenceAggregation.value.includes('true')
        }

        const result = await EventApiService.getMatchReferenceData(this.eventParentDetails.data.id, data)

        this.matchReferenceStats.data = result

        this.matchReferenceStats.maxValues = Object.fromEntries(Object.keys(result[0]?.player_stats || {}).map(key => [key, 100]))
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.match_reference_stats.error'),
          color: 'error'
        })
      } finally {
        this.matchReferenceStats.loading = false
      }
    },
    saveLayoutVariables: debounce(async function () {
      if (!this.hasLayoutManagement) return

      this.submitting = true

      try {
        const data = {
          variables: this.tableHeaders.map(v => ({
            id: v.id,
            order: v.order
          }))
        }

        await LayoutApiService.saveVariables(this.selectedLayout.id, data)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.variables_save.error'),
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    }, 1000),
    getVariableName(value) {
      return this.variables.data.find(variable => variable.value === value)?.text
    },
    getVariableSuffix(value) {
      return this.variables.data.find(variable => variable.value === value)?.suffix
    },
    openAddLayoutDialog() {
      EventBus.$emit(this.$events.ADD_LAYOUT, {
        dashboard: this.isIndividual ? DashboardType.PLAYER_PERFORMANCE : DashboardType.TEAM_EVENT_PERFORMANCE
      })
    },
    openEditLayoutDialog() {
      EventBus.$emit(this.$events.EDIT_LAYOUT, {
        dashboard: this.isIndividual ? DashboardType.PLAYER_PERFORMANCE : DashboardType.TEAM_EVENT_PERFORMANCE,
        layouts: this.layoutsDashboard,
        selectedLayout: this.selectedLayout
      })
    },
    updateSelectedDiagnosticVariables(data) {
      console.log(this.diagnosticVariables.selected)
      console.log('updateSelectedDiagnosticVariables', data)

      this.diagnosticVariables.selected = data
    }
}
}
</script>

<style scoped>

</style>
